/*
 * tl-blog-reader.js — blog article reader shell
 * Loaded only by sections/main-article.liquid. Replaces article.js + tl-article-toc.js
 * on this template.
 *
 * Owns six things:
 *
 * 1. TABLE OF CONTENTS. Built from the h2s inside the rendered article body, so
 *    nothing has to be authored in Shopify. Headings without an id get a slug.
 *    The same list fills the desktop rail and the <details> that replaces it
 *    below 1080px.
 *
 * 2. SCROLL OFFSET. The announcement bar (46px) and header (179px) stack when
 *    pinned, and the header hides on scroll-down / reveals on scroll-up, so the
 *    covered strip changes from moment to moment. We take the WORST case — the
 *    tallest bar CAPABLE of pinning, never below MIN_OFFSET — and publish it as
 *    --tl-offset. Overshooting only leaves air above a heading; undershooting
 *    hides it behind the header.
 *
 * 3. READING PROGRESS. Measured across the article body, not the document, so
 *    the bar reads 100% at the end of the text rather than the end of the footer.
 *
 * 4. READER SETTINGS. Text size, typeface, background theme, line spacing,
 *    column width, focus mode, images on/off. Every visual rule lives in
 *    secondary-css.css.liquid; this file only sets state.
 *
 * 5. STATE ON <html>, NOT ON THE SECTION. The progress bar, settings panel and
 *    "Aa" pill are position:fixed, which silently anchors to the nearest
 *    transformed ancestor instead of the viewport — and theme sections animate.
 *    So those three nodes are re-parented to <body> on boot, and all custom
 *    properties + state classes are written to documentElement so they still
 *    reach them.
 *
 * 6. PERSISTENCE. Preferences are stored under one localStorage key and
 *    reapplied on the next article. Private-mode failures are swallowed.
 */
(function () {
  'use strict';

  var SIZES = [16, 17.5, 19, 21, 23.5];
  var SPACING = { snug: 1.45, normal: 1.65, loose: 1.92 };
  var MEASURE = { narrow: 620, normal: 720, wide: 860 };
  var THEMES = { paper: 1, white: 1, sepia: 1, night: 1 };
  var SERIF_STACK = "'Source Serif 4', Georgia, 'Times New Roman', serif";
  var SERIF_CSS =
    'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&display=swap';
  var STORE_KEY = 'toprank-reader-prefs';

  var MIN_OFFSET = 250; // 46 announcement + 179 header + breathing room
  var MAX_OFFSET = 340; // guard against a mis-measured full-page element
  var MOBILE = 760;

  var html = document.documentElement;
  var root, articleEl, bodyEl, panel, defaults;
  var p = null;
  var headings = [];
  var links = [];
  var offset = MIN_OFFSET;
  var lockUntil = 0;
  var lockedId = null;
  var serifLoaded = false;
  var raf = 0;

  ready(init);

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function init() {
    root = document.getElementById('tl-reader');
    if (!root) return;
    articleEl = root.querySelector('[data-reader-article]');
    bodyEl = root.querySelector('[data-article-body]');

    detachFixed();
    panel = document.querySelector('[data-reader-panel]');

    defaults = {
      size: 2,
      font: 'sans',
      theme: THEMES[root.dataset.theme] ? root.dataset.theme : 'paper',
      spacing: 'normal',
      measure: 'normal',
      focus: false,
      images: true
    };
    p = restore();

    buildToc();
    bind();
    measure();
    apply();
    track();

    // Webfonts and lazy images settle after first paint and move the sticky stack.
    window.addEventListener('load', function () {
      measure();
      track();
    });
  }

  // position:fixed resolves against a transformed ancestor, and theme sections
  // animate. Hoist the three fixed nodes out to <body> so they cannot be caught.
  function detachFixed() {
    var nodes = root.querySelectorAll('.tl-reader__progress, .tl-reader__panel, .tl-reader__pill');
    for (var i = 0; i < nodes.length; i++) document.body.appendChild(nodes[i]);
  }

  /* ---- preferences ------------------------------------------------------- */

  function restore() {
    var out = {};
    for (var k in defaults) out[k] = defaults[k];
    if (root.dataset.remember !== 'true') return out;
    try {
      var saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      if (saved) {
        for (var key in out) if (key in saved) out[key] = saved[key];
      }
    } catch (e) {}
    if (!THEMES[out.theme]) out.theme = defaults.theme;
    if (!SPACING[out.spacing]) out.spacing = 'normal';
    if (!MEASURE[out.measure]) out.measure = 'normal';
    if (out.font !== 'serif') out.font = 'sans';
    out.size = Math.max(0, Math.min(SIZES.length - 1, Number(out.size) || 0));
    out.focus = !!out.focus;
    out.images = out.images !== false;
    return out;
  }

  function save() {
    if (root.dataset.remember !== 'true') return;
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(p));
    } catch (e) {}
  }

  /* ---- table of contents ------------------------------------------------- */

  function slugify(text) {
    var s = String(text)
      .toLowerCase()
      .replace(/['’"]/g, '')
      .replace(/[^a-z0-9À-ɏ]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    return s || 'section';
  }

  function buildToc() {
    var lists = root.querySelectorAll('[data-toc-list]');
    var holders = root.querySelectorAll('[data-toc-holder]');
    var i;
    if (!bodyEl || !lists.length) return;

    var found = bodyEl.querySelectorAll('h2');
    var used = {};

    for (i = 0; i < found.length; i++) {
      var h = found[i];
      var label = (h.textContent || '').trim();
      if (!label) continue;
      var id = h.id;
      if (!id) {
        id = slugify(label);
        while (used[id] || document.getElementById(id)) id = id + '-' + (i + 1);
        h.id = id;
      }
      used[id] = true;
      headings.push({ id: id, el: h });
    }

    // One section is not a table of contents. Drop the rail with it, so a short
    // post does not sit off-centre next to an empty column.
    if (headings.length < 2) {
      for (i = 0; i < holders.length; i++) holders[i].hidden = true;
      var aside = root.querySelector('[data-reader-aside]');
      if (aside) aside.hidden = true;
      return;
    }

    for (i = 0; i < lists.length; i++) {
      var frag = document.createDocumentFragment();
      for (var j = 0; j < headings.length; j++) {
        var a = document.createElement('a');
        a.className = 'tl-reader__toc-link';
        a.href = '#' + headings[j].id;
        a.setAttribute('data-toc', headings[j].id);
        a.textContent = headings[j].el.textContent.trim();
        frag.appendChild(a);
        links.push(a);
      }
      lists[i].appendChild(frag);
    }
    html.classList.add('tl-has-toc');
  }

  /* ---- sticky stack ------------------------------------------------------ */

  // Tallest bar that CAN pin to the top, whether or not it is pinned right now.
  function stickyCapableHeight() {
    var tallest = 0;
    var bars = document.querySelectorAll('header, [id*="announcement"], [id*="__header"]');
    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      var pos = getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') continue;
      var r = el.getBoundingClientRect();
      if (r.width < window.innerWidth * 0.5) continue; // not a full-width bar
      if (r.height > window.innerHeight * 0.5) continue; // not a bar at all
      if (r.height > tallest) tallest = r.height;
    }
    return Math.round(tallest);
  }

  function measure() {
    if (window.innerWidth < MOBILE) {
      offset = 96;
    } else {
      offset = Math.min(MAX_OFFSET, Math.max(MIN_OFFSET, stickyCapableHeight() + 24));
    }
    html.style.setProperty('--tl-offset', offset + 'px');
  }

  /* ---- state ------------------------------------------------------------- */

  function apply() {
    var size = SIZES[p.size];
    if (window.innerWidth < MOBILE) size = Math.round(size * 0.9 * 10) / 10;

    html.style.setProperty('--tl-fs', size + 'px');
    html.style.setProperty('--tl-lh', String(SPACING[p.spacing]));
    html.style.setProperty('--tl-measure', MEASURE[p.measure] + 'px');
    html.style.setProperty('--tl-font', p.font === 'serif' ? SERIF_STACK : 'inherit');

    html.setAttribute('data-tl-theme', p.theme);
    html.classList.toggle('tl-focus', !!p.focus);
    html.classList.toggle('tl-noimg', !p.images);

    if (p.font === 'serif') loadSerif();

    var i, el, parts, key, val, on;
    var opts = document.querySelectorAll('[data-opt]');
    for (i = 0; i < opts.length; i++) {
      el = opts[i];
      parts = el.dataset.opt.split(':');
      key = parts[0];
      val = parts[1];
      if (key === 'theme' || key === 'font' || key === 'spacing' || key === 'measure') {
        on = p[key] === val;
        el.classList.toggle('is-on', on);
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      } else if (key === 'focus') {
        el.setAttribute('aria-pressed', p.focus ? 'true' : 'false');
      } else if (key === 'images') {
        el.setAttribute('aria-pressed', p.images ? 'true' : 'false');
      }
    }
    var dots = document.querySelectorAll('[data-dot]');
    for (i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-on', Number(dots[i].dataset.dot) <= p.size);
    }
    var switches = document.querySelectorAll('[data-switch]');
    for (i = 0; i < switches.length; i++) {
      on = switches[i].dataset.switch === 'focus' ? p.focus : p.images;
      switches[i].classList.toggle('is-on', !!on);
    }
  }

  function loadSerif() {
    if (serifLoaded) return;
    serifLoaded = true;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = SERIF_CSS;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  function panelOpen() {
    return !!panel && !panel.hidden;
  }

  function setPanel(open) {
    if (!panel) return;
    panel.hidden = !open;
    var toggles = document.querySelectorAll('[data-opt="panel:toggle"]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  /* ---- progress + active heading ----------------------------------------- */

  function track() {
    var bar = document.querySelector('[data-progress]');
    if (bar && articleEl) {
      var rect = articleEl.getBoundingClientRect();
      var start = rect.top + window.scrollY;
      var span = rect.height - window.innerHeight * 0.6;
      var pct = span > 0 ? ((window.scrollY - start) / span) * 100 : 0;
      bar.style.width = Math.min(100, Math.max(0, pct)) + '%';
    }

    if (!links.length) return;
    var activeId = headings[0].id;
    for (var i = 0; i < headings.length; i++) {
      if (headings[i].el.getBoundingClientRect().top <= offset + 20) activeId = headings[i].id;
    }
    if (Date.now() < lockUntil && lockedId) activeId = lockedId;
    for (var j = 0; j < links.length; j++) {
      var on = links[j].dataset.toc === activeId;
      links[j].classList.toggle('is-active', on);
      if (on) links[j].setAttribute('aria-current', 'true');
      else links[j].removeAttribute('aria-current');
    }
  }

  function jumpTo(id) {
    var target = document.getElementById(id);
    if (!target) return;
    lockedId = id;
    lockUntil = Date.now() + 900;
    var top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    if (history.replaceState) history.replaceState(null, '', '#' + id);
  }

  /* ---- events ------------------------------------------------------------ */

  function bind() {
    document.addEventListener('click', onClick);

    window.addEventListener(
      'scroll',
      function () {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          track();
        });
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      function () {
        measure();
        apply();
        track();
      },
      { passive: true }
    );

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panelOpen()) setPanel(false);
    });
  }

  function onClick(e) {
    if (!e.target.closest) return;

    var link = e.target.closest('[data-toc]');
    if (link) {
      e.preventDefault();
      jumpTo(link.dataset.toc);
      track();
      return;
    }

    var hit = e.target.closest('[data-opt]');
    if (!hit) {
      if (panelOpen() && !e.target.closest('[data-reader-panel]')) setPanel(false);
      return;
    }

    e.preventDefault();
    var parts = hit.dataset.opt.split(':');
    var key = parts[0];
    var val = parts[1];

    if (key === 'panel') {
      setPanel(val === 'toggle' ? !panelOpen() : false);
      return;
    }
    if (key === 'size') p.size = Math.max(0, Math.min(SIZES.length - 1, p.size + (val === 'up' ? 1 : -1)));
    else if (key === 'font') p.font = val;
    else if (key === 'theme') p.theme = val;
    else if (key === 'spacing') p.spacing = val;
    else if (key === 'measure') p.measure = val;
    else if (key === 'focus') {
      p.focus = !p.focus;
      if (!p.focus) p.images = true;
    } else if (key === 'images') p.images = !p.images;
    else if (key === 'reset') {
      for (var k in defaults) p[k] = defaults[k];
    }

    apply();
    save();
    track();
  }
})();
